import { utcToZonedTime } from 'date-fns-tz'
import { parse } from 'iso8601-duration'
import { RRule, Frequency as RRuleFrequency } from 'rrule'
import {
  IncentivePlanStatus as ApiIncentivePlanStatus,
  IncentiveType as ApiIncentiveType,
  MerchantEligibilityType,
  MerchantListGroup,
  MerchantRadiusUnit as ApiMerchantRadiusUnit,
  Schedule,
  ScheduleStatus,
  WorkspaceConnectionListGroup,
  WorkspaceMemberListGroup,
} from 'lib/generated/api'
import {
  IncentivePlanSchedule,
  IncentivePlanStatus,
  IncentiveType,
  MerchantParticipationType,
  MerchantRadiusUnit,
  ScheduleFrequency,
  ScheduleOccurrence,
  ScheduleType,
} from 'types/entities'

export const TIME_FORMAT = 'HH:mm'

export const scheduleStatusMap: Record<ScheduleStatus, IncentivePlanStatus> = {
  UPCOMING: 'upcoming',
  ONGOING: 'live',
  ENDED: 'ended',
}

export const incentiveTypeMap: Partial<
  Record<ApiIncentiveType, IncentiveType>
> = {
  DISCOUNT_AMOUNT: 'discountAmount',
  DISCOUNT_PERCENTAGE: 'discountPercentage',
}

export const merchantEligibilityTypeMap: Record<
  MerchantEligibilityType,
  MerchantParticipationType
> = {
  ALL_MERCHANTS: 'all_merchants',
  WORKSPACE: 'workspace',
  RADIUS: 'radius',
  MERCHANT_LISTS: 'merchant_list',
}

export const merchantRadiusUnitMap: Record<
  ApiMerchantRadiusUnit,
  MerchantRadiusUnit
> = {
  KILOMETER: 'kilometer',
  MILE: 'mile',
}

export const frequencyMap: Record<RRuleFrequency, ScheduleFrequency> = {
  [RRule.YEARLY]: 'yearly',
  [RRule.MONTHLY]: 'monthly',
  [RRule.WEEKLY]: 'weekly',
  [RRule.DAILY]: 'daily',
  [RRule.HOURLY]: 'daily',
  [RRule.MINUTELY]: 'daily',
  [RRule.SECONDLY]: 'daily',
}

export const scheduleTypeMap: Record<RRuleFrequency, ScheduleType> = {
  [RRule.YEARLY]: 'yearly',
  [RRule.MONTHLY]: 'monthly',
  [RRule.WEEKLY]: 'weekly',
  [RRule.DAILY]: 'daily',
  [RRule.HOURLY]: 'none',
  [RRule.MINUTELY]: 'none',
  [RRule.SECONDLY]: 'none',
}

export const occurrenceMap: Record<number, ScheduleOccurrence> = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  [-1]: 'last',
}

export const dayMapper = (rruleDay: number): number => {
  if (rruleDay === 6) return 0
  return rruleDay + 1
}

export const computeCompanies = (
  group?: WorkspaceConnectionListGroup
): string[] => {
  if (!group || group.connectionLists.length < 1) return []
  return group.connectionLists[0].connections.map(
    connection => connection.connectionId
  )
}

export const computeMerchantList = (
  group?: MerchantListGroup
): string | undefined => {
  if (!group || group.merchantLists.length < 1) return undefined
  return group.merchantLists[0].merchantListId
}

export const computePeople = (group?: WorkspaceMemberListGroup): string[] => {
  if (!group || group.memberLists.length < 1) return []
  return group.memberLists[0].members.map(member => member.workspaceMemberId)
}

export const computeStatus = (
  status: ApiIncentivePlanStatus,
  scheduleStatus?: ScheduleStatus
): IncentivePlanStatus => {
  if (status === 'PAUSED') return 'paused'
  if (status === 'DRAFT') return 'draft'
  if (scheduleStatus) return scheduleStatusMap[scheduleStatus]
  // This should never happen
  return 'draft'
}

export const computeSchedule = (
  defaultTimezone: string,
  schedule?: Schedule,
  availableFrom?: string,
  availableTo?: string,
  duration?: string
): IncentivePlanSchedule | undefined => {
  if (!schedule) return undefined

  const nowDate = new Date()
  const values: IncentivePlanSchedule = {
    type: 'none',
    start: nowDate,
    timezone: defaultTimezone,
    fromTime: availableFrom || '09:00',
    toTime: availableTo || '17:00',
    interval: 2,
    customFrequency: 'daily',
    days: [1, 2, 3, 4, 5], // Monday to Friday
    weekStart: 1, // Monday
    weekRange: 5,
    monthlyOccurrence: 'first',
    customOccurrence: 'sameMonthDay',
    ending: 'never',
    endingOn: nowDate,
  }

  const { start, recurrenceRule } = schedule

  // Use the provided time zone if time zone is missing from the response
  const tz = schedule?.timezone || defaultTimezone
  values.timezone = tz

  if (start?.dateTime) {
    const startDate = utcToZonedTime(start.dateTime, tz)
    values.start = startDate
    values.endingOn = startDate
  }

  if (recurrenceRule) {
    const options = RRule.parseString(recurrenceRule)
    const rule = new RRule(options)
    const { interval, byweekday, bynweekday, bymonthday, until } = rule.options

    if (options.freq === RRule.WEEKLY) {
      const parsedDuration = duration ? parse(duration) : undefined
      values.weekRange = parsedDuration?.days || 5
      // if the frequency is weekly, treat byweekday as the start of the week
      values.weekStart =
        byweekday?.[0] !== undefined ? dayMapper(byweekday[0]) : 1
    } else if (byweekday) {
      // otherwise, treat byweekday normally
      values.days = byweekday.map(dayMapper)
    }

    if (interval > 1) {
      values.type = 'custom'
      values.interval = interval
      if (options.freq) {
        values.customFrequency = frequencyMap[options.freq]
      }
      if (options.freq === RRule.MONTHLY) {
        if (bymonthday) {
          values.customOccurrence = 'sameMonthDay'
        }
        if (bynweekday) {
          values.customOccurrence = 'sameWeekDay'
        }
      }
    } else {
      if (options.freq) {
        values.type = scheduleTypeMap[options.freq]
      }
      if (bynweekday) {
        values.monthlyOccurrence = occurrenceMap[bynweekday[0][1]] || 'first'
      }
    }

    if (until) {
      values.ending = 'date'
      values.endingOn = utcToZonedTime(until, tz)
    }
  }
  return values
}

export const parseIncentiveType = (
  incentiveType?: ApiIncentiveType,
  scheduleType?: ScheduleType
): IncentiveType | undefined => {
  if (!incentiveType) return undefined
  if (incentiveType === ApiIncentiveType.Credit) {
    if (!scheduleType || scheduleType === 'none') return 'creditOneTime'
    return 'creditRecurring'
  }
  return incentiveTypeMap[incentiveType]
}
